import { useCallback, useEffect, useMemo } from "react"
import { useTheme, Box } from "@mui/material"
import { get, isNil } from "lodash"
import { useSearchParams } from "react-router-dom"

import { GTM_AMENITY_SEARCH } from "constant"
import { pushDataLayer } from "utils/googletagmanager"

import { useGeoLocation } from "providers/geolocation"
import { useFeatureCategory } from "hooks/feature"
import { useGetMapViewFromUrl } from "hooks/map"
import { useVenue } from "providers/venue"
import {
  Feature,
  useHighLightFeaturesMap,
} from "hooks/feature/useHighLightFeaturesMap"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"

import { PageLayout } from "layouts"
import { FeatureChipToggleBar } from "components/domains/features/ChipToggleBar"
import { HeaderLogo } from "components/Header"
import {
  Header,
  HeaderMenuButton,
  HeaderSearchButton,
} from "extensions/one-siam/components/Header"
import { AppNavigation } from "extensions/one-siam/container/AppNavigation"
import { OrdinalSelector } from "extensions/one-siam/components/OrdinalSelector"
import { useMap } from "hooks/map/useMap"

export const FeatureByCategory = ({ Layout = PageLayout }) => {
  const theme = useTheme()
  const { locationVenue, locationOrdinal } = useGeoLocation()
  const locationVenueId = get(locationVenue, "id")
  const [searchParams, setSearchParams] = useSearchParams()
  const { isMultiOrdinalVenue, dataLoaded } = useVenue()
  const { viewingVenueLevels } = useMap()
  const {
    quickLinkContainer, //TODO:: planning about quick search's working flow later
    headerWrapper,
    ordinalSelectMenu,
    navigationWrapper,
  } = theme.components[`AppMap`]?.styleOverrides || {}

  const {
    viewingCategory,
    viewingOrdinal,
    comingFromOrdinal,
    featureCategories,
    searchableFeatures,
    handleClickQuickSearch,
  } = useFeatureCategory({
    initialVenueId: locationVenueId,
    initialOrdinal: locationOrdinal,
  })

  const enableMap =
    !isNil(viewingOrdinal) && isNil(comingFromOrdinal) && dataLoaded

  const { mapView } = useGetMapViewFromUrl()

  const { flyTo } = useIndoorMap()

  useEffect(() => {
    flyTo(mapView)
  }, [flyTo, mapView])

  const onClickOrdinal = useCallback(
    (ordinal) => {
      const currentParams = Object.fromEntries(searchParams.entries())
      setSearchParams({ ...currentParams, ordinal })
    },
    [setSearchParams, searchParams]
  )

  const parsedOrdinal = useMemo(() => Number(viewingOrdinal), [viewingOrdinal])

  useHighLightFeaturesMap(searchableFeatures as Feature[], parsedOrdinal, {
    enabled: enableMap,
  })

  useEffect(() => {
    if (viewingCategory) {
      pushDataLayer({
        event: GTM_AMENITY_SEARCH,
        amenity_category: viewingCategory,
      })
    }
  }, [viewingCategory])

  return (
    <Layout
      header={
        <Box sx={headerWrapper}>
          <Header
            logo={<HeaderLogo />}
            menu={<HeaderMenuButton />}
            search={<HeaderSearchButton />}
          />
        </Box>
      }
    >
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelectMenu}>
          <OrdinalSelector
            selectedOrdinal={Number(viewingOrdinal)}
            levels={viewingVenueLevels}
            onChangeOrdinal={onClickOrdinal}
          />
        </Box>
      )}
      <Box sx={navigationWrapper}>
        <Box sx={quickLinkContainer}>
          <FeatureChipToggleBar
            categories={featureCategories}
            onClick={handleClickQuickSearch}
          />
        </Box>
        <AppNavigation />
      </Box>
    </Layout>
  )
}
